import styles from 'legacy/common/components/form/Form.module.scss';

import * as Yup from 'yup';
import classnames from 'classnames';
import React from 'react';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import { getCallingCode, getCountryCode } from 'legacy/common/utils/country';

import FormWrapper from '../FormWrapper';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import PhoneInput from 'legacy/common/components/phone-input/PhoneInput';
import { SUPPORTED_COUNTRY_OPTIONS } from 'legacy/common/constants/countries';

const SignUpSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email'),
  phoneNumber: Yup.string()
    .min(5, 'Must be at least 5 digits')
    .required('Required'),
  accountCountryCode: Yup.string().required('Required')
});

type NewSignUpFormProps = {
  onSubmit: (values: {
    phoneNumber: string;
    phoneCountryCode: string;
    callingCode: string;
    email: string;
    firstName: string;
    lastName: string;
    accountCountryCode: string;
  }) => void;
  onClickLogin?: () => void;
  isLoading: boolean;
  errorMessage: string;
};

export const SignUpForm: React.FC<NewSignUpFormProps> = ({
  errorMessage,
  onSubmit,
  onClickLogin,
  isLoading
}) => (
  <NewSignUpForm
    errorMessage={errorMessage}
    onSubmit={onSubmit}
    onClickLogin={onClickLogin}
    isLoading={isLoading}
    isModal={false}
  />
);
export const SignUpFormModalContent: React.FC<NewSignUpFormProps> = ({
  errorMessage,
  onSubmit,
  onClickLogin,
  isLoading
}) => (
  <NewSignUpForm
    errorMessage={errorMessage}
    onSubmit={onSubmit}
    onClickLogin={onClickLogin}
    isLoading={isLoading}
    isModal
  />
);

const NewSignUpForm: React.FC<NewSignUpFormProps & { isModal: boolean }> = ({
  errorMessage,
  onSubmit,
  onClickLogin,
  isLoading,
  isModal
}) => {
  const { t } = useTranslation('auth');
  const currentPhoneCountryCode = getCountryCode();
  const currentCallingCode = getCallingCode(currentPhoneCountryCode);

  return (
    <FormWrapper title="Welcome to Bounce" isModal={isModal}>
      <p className={styles.submitError}>{errorMessage}</p>

      <Formik
        initialValues={{
          password: '',
          phoneNumber: '',
          passwordConfirmation: '',
          firstName: '',
          lastName: '',
          email: '',
          phoneCountryCode: currentPhoneCountryCode,
          callingCode: currentCallingCode,
          accountCountryCode: ''
        }}
        onSubmit={onSubmit}
        validationSchema={SignUpSchema}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          setFieldValue,
          handleBlur,
          handleChange
        }) => (
          <Form>
            <div className={styles.group}>
              <p className={classnames(styles.label, styles.small)}>
                First Name
              </p>
              <div className={styles.relative}>
                <Field
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder={t('placeholders.enterFirstName')}
                  className={classnames(styles.input, {
                    [styles.errorField]: errors.firstName && touched.firstName
                  })}
                  data-testid="input-first-name"
                />
                {errors.firstName && touched.firstName && (
                  <div className={classnames(styles.error, styles.right)}>
                    {errors.firstName}
                  </div>
                )}
              </div>

              <p className={classnames(styles.label, styles.small)}>
                Last Name
              </p>
              <div className={styles.relative}>
                <Field
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder={t('placeholders.enterLastName')}
                  className={classnames(styles.input, {
                    [styles.errorField]: errors.lastName && touched.lastName
                  })}
                  data-testid="input-last-name"
                />
                {errors.lastName && touched.lastName && (
                  <div className={classnames(styles.error, styles.right)}>
                    {errors.lastName}
                  </div>
                )}
              </div>

              <p className={classnames(styles.label, styles.small)}>Email</p>
              <div className={styles.relative}>
                <Field
                  id="email"
                  name="email"
                  type="text"
                  placeholder={t('placeholders.enterEmail')}
                  className={classnames(styles.input, {
                    [styles.errorField]: errors.email && touched.email
                  })}
                  data-testid="input-email"
                />
                {errors.email && touched.email && (
                  <div className={classnames(styles.error, styles.right)}>
                    {errors.email}
                  </div>
                )}
              </div>

              <p className={classnames(styles.label, styles.small)}>
                Phone number
              </p>
              <PhoneInput
                value={values.phoneNumber}
                hasError={errors.phoneNumber && touched.phoneNumber}
                testId="input-phone-number"
                countryCode={values.phoneCountryCode}
                onCountryChange={(selected) => {
                  setFieldValue('phoneCountryCode', selected.countryCode);
                  setFieldValue('callingCode', selected.value);
                }}
                onChange={handleChange('phoneNumber')}
                onBlur={handleBlur('phoneNumber')}
              />
              {errors.phoneNumber && touched.phoneNumber && (
                <div className={classnames(styles.error, styles.right)}>
                  {errors.phoneNumber}
                </div>
              )}
            </div>

            <p className={classnames(styles.label, styles.small)}>
              Which country will you attend events in?
            </p>
            <div className={styles.relative}>
              <Field
                as="select"
                name="accountCountryCode"
                className={classnames(styles.select, {
                  [styles.errorField]:
                    errors.accountCountryCode && touched.accountCountryCode
                })}
                data-testid="input-account-country"
              >
                <option value="">{t('placeholders.selectCountry')}</option>
                {SUPPORTED_COUNTRY_OPTIONS.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.text}
                  </option>
                ))}
              </Field>
              {errors.accountCountryCode && touched.accountCountryCode && (
                <div className={classnames(styles.error, styles.right)}>
                  {errors.accountCountryCode}
                </div>
              )}
            </div>

            <div className="mt-10">
              <Button
                testId="button-submit"
                isSubmit
                disabled={!isValid}
                loading={isLoading}
                type={ButtonType.Primary}
              >
                Sign Up
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      <div className="mt-10 mb-6">
        {t('common.alreadyHaveAccount')}
        <span
          data-testid="button-log-in"
          className="ml-1 text-primary-500 cursor-pointer"
          onClick={onClickLogin}
        >
          {t('buttons.logIn')}
        </span>
      </div>
    </FormWrapper>
  );
};
